import { Dispatch, FC, SetStateAction, useContext, useMemo } from 'react';
import router from 'next/router';
import { useNetwork } from '@hooks/useNetwork';
import { Context } from '@context/context';
import { IState, ProcessInterruptLabels } from '@context/stateModels';
import { useCommonLabels } from '@hooks/useCommonLabels';
import { STEPS_BEFORE_CRIF_CONTRIBUTION } from './constants';
import { Field } from '@pages/api/services';
import { apiErrorHandler, handleResReportAndErrorsInformation } from '@utils/ApiErrorsManaging';

import styles from './InterruptProcess.module.scss';

export const PROCESS_INTERRUPT_ERROR = 'PROCESS_INTERRUPT_ERROR';

export type KnownError = {
  segnalazione: string;
};

type InterruptProcessProps = {
  saveProcessData?: () => Promise<void>;
  redirectToPublicSite: () => void;
  setDialogState: Dispatch<SetStateAction<boolean>>;
};

const isKnownError = (err: unknown | KnownError): err is KnownError => {
  return (err as KnownError)?.segnalazione !== undefined;
};

export const InterruptProcess: FC<InterruptProcessProps> = ({
  saveProcessData,
  redirectToPublicSite,
  setDialogState
}) => {
  const { state, dispatch } = useContext<IState>(Context);
  const { processInterrupt, processResume, processInterruptPostCrif, allErrorMessages } = useCommonLabels();

  const isBeforeCrifContribution = useMemo(() => {
    if (state.coreInformation.isConto) return false;
    return (STEPS_BEFORE_CRIF_CONTRIBUTION as ReadonlyArray<string>).includes(state.stepManager.actualStep);
  }, [state.coreInformation.isConto, state.stepManager.actualStep]);

  const isPostValutaProposta = useMemo(() => {
    if (sessionStorage.getItem('seenValutaProposta') === 'true') return true;
    const { stepList, stepWhereShouldStay } = state.stepManager;
    const valutaIdx = stepList.findIndex(s => s.stepName === 'Valuta proposta');
    if (valutaIdx === -1) return false;
    const resumeIdx = stepList.findIndex(s => s.stepName === stepWhereShouldStay);
    if (resumeIdx === -1) return false;
    return resumeIdx >= valutaIdx;
  }, [state.stepManager]);

  const isCrossOnboardingBankAccountContractSigning = useMemo(() => {
    return state.stepManager.actualStep == 'Firma contratto conto' && !state.coreInformation.isConto;
  }, [state.coreInformation.isConto, state.stepManager.actualStep]);

  const selectedLabels = isBeforeCrifContribution
    ? processResume
    : isPostValutaProposta
    ? processInterruptPostCrif
    : processInterrupt;
  const getDescription = (processInterrupt: ProcessInterruptLabels | undefined) => {
    if (state.coreInformation.isConto) {
      return processInterrupt?.descriptionConto;
    }
    return selectedLabels?.description;
  };

  const bpmId = sessionStorage.getItem('bpmId');

  const { fireRequest: interruptProcessRequest } = useNetwork({
    url: `/lending/onboardings/${bpmId}`,
    method: 'delete',
    field: Field.businessdata,
    coreInformation: state.coreInformation
  });

  const onCancelClick = async () => {
    if (saveProcessData) {
      try {
        await saveProcessData();
      } catch (e) {
        setDialogState(isOpen => !isOpen);
      }
    }
    redirectToPublicSite();
  };

  const handleError = (err: unknown) => {
    if (isKnownError(err)) {
      return handleResReportAndErrorsInformation(err);
    }
    return handleResReportAndErrorsInformation({
      segnalazione: PROCESS_INTERRUPT_ERROR
    });
  };

  const onConfirmClick = async () => {
    dispatch({ type: 'SHOW_LOADER' });

    try {
      dispatch({ type: 'UPDATE_CORE_INFORMATION', payload: { interruptProcess: true } });
      const bpmId = sessionStorage.getItem('bpmId');
      await interruptProcessRequest({ url: `/lending/onboardings/${bpmId}` });
      isBeforeCrifContribution ? router.push('../process-interruption') : router.push('/waiver-success-page');
      dispatch({ type: 'HIDE_LOADER' });
    } catch (err) {
      dispatch({ type: 'UPDATE_CORE_INFORMATION', payload: { interruptProcess: false } });
      apiErrorHandler(dispatch, allErrorMessages, handleError(err));
      dispatch({ type: 'HIDE_LOADER' });
    }
  };

  const onCrossOnboardingContoEliminateClick = () => {
    dispatch({
      type: 'UPDATE_CORE_INFORMATION',
      payload: {
        eliminateXConto: true
      }
    });
    setDialogState(false);
  };

  return (
    <div className={styles.wrapper}>
      <header className={styles.titleSection}>
        <h5 className={styles.exitTitle}>{selectedLabels?.title}</h5>
      </header>
      <section className={styles.titleSection}>
        <h6
          className={`process-interrupt_description ${styles.description} ${styles.exitDescription}`}
          dangerouslySetInnerHTML={{
            __html: getDescription(processInterrupt) ?? ''
          }}
        />
        {isCrossOnboardingBankAccountContractSigning && (
          <h6
            className={`process-interrupt_description ${styles.description} ${styles.exitDescription} ${styles.eliminateAccountDescription}`}
          >
            {processInterrupt?.descriptionRemoveConto}{' '}
            <button
              className={styles.eliminateAccountLink}
              onClick={onCrossOnboardingContoEliminateClick}
              type="button"
              tabIndex={0}
            >
              {processInterrupt?.linkRemoveConto}
            </button>
          </h6>
        )}
      </section>
      {(selectedLabels?.confirmCta || selectedLabels?.cancelCta) && (
        <section className={styles.btnsSection}>
          <div className={styles.btns}>
            {selectedLabels?.cancelCta && (
              <button
                className={`${styles.closebtns} ${styles.notConfirmButton} notConfirmButton`}
                onClick={onCancelClick}
              >
                {selectedLabels?.cancelCta}
              </button>
            )}
            {selectedLabels?.confirmCta && (
              <button className={`${styles.closebtns} ${styles.confirmButton} confirmButton`} onClick={onConfirmClick}>
                {selectedLabels?.confirmCta}
              </button>
            )}
          </div>
        </section>
      )}
    </div>
  );
};
